import { useEffect, useRef, useState } from 'react';
import { createRoute } from '@tanstack/react-router';
import maplibregl from 'maplibre-gl';
import {
  Brain,
  FlaskConical,
  Hexagon,
  MonitorPlay,
  Mountain,
  Plus,
  Ruler,
  Trash2,
  Undo2,
  Video,
  Waves,
  X,
} from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import {
  distM,
  fmtArea,
  fmtDistance,
  pathLengthM,
  polygonAreaM2,
  useAnalysis,
  type AnalysisLayers,
  type AnalysisTool,
} from '@/store/analysis';
import { recordEvent } from '@/store/appEvents';
import { useMapBase } from '@/map/useMapBase';
import { cn } from '@/lib/utils';
import { rootRoute } from './__root';

/* =================================================================== */
/* Layer catalogue — data enters as PMTiles files in the Hub tiles dir. */
/* Convention: /tiles/<name>.pmtiles with the vector source-layer named  */
/* after the file stem. The rail HEAD-checks each file and only offers   */
/* toggles for data that actually exists. See SPEC-0013.                 */
/* =================================================================== */

interface LayerSpec {
  key: keyof AnalysisLayers;
  label: string;
  hint: string;
  file: string;
  icon: React.ComponentType<{ className?: string }>;
}

const LAYER_CATALOGUE: LayerSpec[] = [
  { key: 'hillshade', label: 'DEM hillshade', hint: 'terrain relief', file: 'dem.pmtiles', icon: Mountain },
  { key: 'contours', label: 'Contours', hint: 'elevation lines', file: 'contours.pmtiles', icon: Hexagon },
  { key: 'water', label: 'Water', hint: 'hydrology overlay', file: 'water.pmtiles', icon: Waves },
  { key: 'lithology', label: 'Lithology', hint: 'geology units', file: 'lithology.pmtiles', icon: FlaskConical },
];

function AnalyseWorkspace() {
  const tool = useAnalysis((s) => s.tool);
  const points = useAnalysis((s) => s.points);
  const setTool = useAnalysis((s) => s.setTool);
  const undoPoint = useAnalysis((s) => s.undoPoint);
  const clearPoints = useAnalysis((s) => s.clearPoints);
  const layers = useAnalysis((s) => s.layers);
  const setLayer = useAnalysis((s) => s.setLayer);

  /** Which catalogue files exist on the Hub (HEAD-checked once on mount). */
  const [available, setAvailable] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const out: Record<string, boolean> = {};
      await Promise.all(
        LAYER_CATALOGUE.map(async (l) => {
          try {
            const res = await fetch(`/tiles/${l.file}`, { method: 'HEAD' });
            out[l.key] = res.ok;
          } catch {
            out[l.key] = false;
          }
        }),
      );
      if (!cancelled) setAvailable(out);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="flex h-full bg-base">
      {/* Rail */}
      <aside
        className="flex h-full flex-none flex-col border-r border-border-default bg-surface-1"
        style={{ width: 'var(--rail-left)' }}
      >
        <header className="flex items-center gap-2 border-b border-border-default px-3 py-3">
          <FlaskConical className="h-4 w-4 text-accent" />
          <span className="font-mono text-[13px] font-semibold tracking-[0.04em] text-fg-primary">
            Analyse
          </span>
          <span className="text-[10px] uppercase tracking-[0.14em] text-fg-muted">geodata</span>
        </header>

        <div className="flex-1 space-y-5 overflow-y-auto overflow-x-hidden p-3">
          {/* Tools */}
          <section className="space-y-2">
            <SectionLabel>Tools</SectionLabel>
            <ToolRow
              icon={<Ruler className="h-3.5 w-3.5" />}
              label="Distance path"
              hint="click legs, get per-leg + total"
              active={tool === 'distance'}
              onClick={() => setTool(tool === 'distance' ? 'none' : 'distance')}
            />
            <ToolRow
              icon={<Hexagon className="h-3.5 w-3.5" />}
              label="Area"
              hint="click a polygon, get area + perimeter"
              active={tool === 'area'}
              onClick={() => setTool(tool === 'area' ? 'none' : 'area')}
            />
            <ToolRow
              icon={<Mountain className="h-3.5 w-3.5" />}
              label="Volumetric"
              hint="needs a DEM + survey DSM — coming with the survey pipeline"
              active={false}
              disabled
              onClick={() => {}}
            />
            {tool !== 'none' && points.length > 0 && (
              <div className="flex gap-1.5">
                <button type="button" onClick={undoPoint} className={miniBtnCls}>
                  <Undo2 className="h-3 w-3" /> Undo
                </button>
                <button type="button" onClick={clearPoints} className={miniBtnCls}>
                  <Trash2 className="h-3 w-3" /> Clear
                </button>
              </div>
            )}
          </section>

          {/* Layers */}
          <section className="space-y-2">
            <SectionLabel>Layers</SectionLabel>
            {LAYER_CATALOGUE.map((l) => {
              const isAvail = available[l.key] === true;
              const Icon = l.icon;
              return (
                <div key={l.key} className="flex items-center gap-2">
                  <Icon className={cn('h-3.5 w-3.5 flex-none', isAvail ? 'text-fg-secondary' : 'text-fg-disabled')} />
                  <div className="min-w-0 flex-1">
                    <div className={cn('text-[11.5px]', isAvail ? 'text-fg-primary' : 'text-fg-muted')}>
                      {l.label}
                    </div>
                    <div className="truncate text-[9.5px] text-fg-muted">
                      {isAvail ? l.hint : `drop ${l.file} into the Hub tiles dir`}
                    </div>
                  </div>
                  <Toggle
                    checked={isAvail && layers[l.key]}
                    disabled={!isAvail}
                    onChange={(v) => setLayer(l.key, v)}
                  />
                </div>
              );
            })}
            <p className="text-[9.5px] leading-relaxed text-fg-muted">
              Layers are PMTiles files served by the Hub from its tiles directory. Survey
              products from the analysis worker land there too.
            </p>
          </section>

          {/* Streams */}
          <StreamsSection />
        </div>
      </aside>

      {/* Map */}
      <section className="relative flex-1 overflow-hidden">
        <AnalysisMap available={available} />
        <div className="pointer-events-none absolute inset-x-0 top-3 z-10 flex justify-center px-3">
          <div className="flex items-center gap-2 rounded-sm border border-border-default bg-base/85 px-3 py-1.5 font-mono text-[10.5px] text-fg-secondary shadow-[var(--shadow-pop)] backdrop-blur-md">
            <span className="font-semibold uppercase tracking-[0.16em]" style={{ color: 'var(--color-accent)' }}>
              {tool === 'none' ? 'Analyse' : tool === 'distance' ? 'Distance' : 'Area'}
            </span>
            <span className="hidden h-3 w-px bg-border-default sm:inline" aria-hidden />
            <span>
              {tool === 'none'
                ? 'pick a tool on the left'
                : tool === 'distance'
                  ? 'click to add path points'
                  : 'click at least 3 corners'}
            </span>
          </div>
        </div>
        <MeasureReadout />
        <StreamViewer />
      </section>
    </div>
  );
}

/* =================================================================== */
/* Streams — URL-registered until the Hub grows a WebRTC relay.         */
/* MJPEG (multipart) URLs render in <img>; HLS/MP4 in <video>.          */
/* =================================================================== */

function StreamsSection() {
  const streams = useAnalysis((s) => s.streams);
  const addStream = useAnalysis((s) => s.addStream);
  const removeStream = useAnalysis((s) => s.removeStream);
  const openStream = useAnalysis((s) => s.openStream);
  const openId = useAnalysis((s) => s.openStreamId);
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');

  function add() {
    if (!/^https?:\/\//.test(url.trim())) return;
    addStream(name.trim() || `stream ${streams.length + 1}`, url.trim());
    recordEvent('system', `stream registered: ${name.trim() || url.trim().slice(0, 40)}`);
    setUrl('');
    setName('');
  }

  return (
    <section className="space-y-2">
      <SectionLabel>Live streams</SectionLabel>
      {streams.length === 0 && (
        <p className="text-[10px] leading-relaxed text-fg-muted">
          Register a camera/thermal feed by URL — MJPEG or HLS/MP4 (e.g. a companion-computer
          or IP-camera endpoint). A Hub-side WebRTC relay comes later.
        </p>
      )}
      <ul className="space-y-1">
        {streams.map((st) => (
          <li
            key={st.id}
            className={cn(
              'group flex items-center gap-2 rounded border px-2 py-1.5',
              openId === st.id ? 'border-accent bg-accent/8' : 'border-border-subtle bg-surface-2/40',
            )}
          >
            <Video className={cn('h-3.5 w-3.5 flex-none', openId === st.id ? 'text-accent' : 'text-fg-muted')} />
            <button
              type="button"
              onClick={() => openStream(openId === st.id ? null : st.id)}
              className="min-w-0 flex-1 text-left"
              title={st.url}
            >
              <div className="truncate text-[11.5px] text-fg-primary">{st.name}</div>
              <div className="truncate font-mono text-[9px] text-fg-muted">{st.url}</div>
            </button>
            <button
              type="button"
              onClick={() => removeStream(st.id)}
              title="Remove stream"
              className="grid h-5 w-5 flex-none place-items-center rounded text-fg-muted opacity-0 transition-opacity hover:bg-surface-3 hover:text-status-critical group-hover:opacity-100"
            >
              <X className="h-3 w-3" />
            </button>
          </li>
        ))}
      </ul>
      <div className="space-y-1.5">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="name (e.g. auto-1 thermal)"
          className={inputCls}
        />
        <div className="flex gap-1.5">
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && add()}
            placeholder="http://…/stream.mjpg"
            className={cn(inputCls, 'flex-1')}
          />
          <button
            type="button"
            onClick={add}
            disabled={!/^https?:\/\//.test(url.trim())}
            className="grid h-7 w-7 flex-none place-items-center rounded border border-border-default text-fg-secondary hover:bg-surface-2 hover:text-fg-primary disabled:opacity-40"
            title="Add stream"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <div className="flex items-center gap-2 rounded border border-dashed border-border-default px-2 py-1.5">
        <Brain className="h-3.5 w-3.5 flex-none text-fg-disabled" />
        <div className="min-w-0 flex-1">
          <div className="text-[11px] text-fg-muted">AI overlay on stream</div>
          <div className="text-[9px] text-fg-muted">detector worker — coming with the analysis pipeline</div>
        </div>
        <Toggle checked={false} disabled onChange={() => {}} />
      </div>
    </section>
  );
}

/** Floating viewer bottom-right of the map. MJPEG via <img>, else <video>. */
function StreamViewer() {
  const openId = useAnalysis((s) => s.openStreamId);
  const streams = useAnalysis((s) => s.streams);
  const openStream = useAnalysis((s) => s.openStream);
  const st = streams.find((x) => x.id === openId);
  if (!st) return null;
  const isVideo = /\.(m3u8|mp4|webm)(\?|$)/i.test(st.url);
  return (
    <div className="absolute bottom-8 right-3 z-10 w-[360px] overflow-hidden rounded-lg border border-border-default bg-base/95 shadow-[var(--shadow-pop)] backdrop-blur-md">
      <header className="flex items-center gap-2 border-b border-border-subtle px-2.5 py-1.5">
        <MonitorPlay className="h-3.5 w-3.5 text-accent" />
        <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-fg-primary">{st.name}</span>
        <Badge tone="critical">LIVE</Badge>
        <button
          type="button"
          onClick={() => openStream(null)}
          className="grid h-5 w-5 place-items-center rounded text-fg-muted hover:bg-surface-2 hover:text-fg-primary"
        >
          <X className="h-3 w-3" />
        </button>
      </header>
      <div className="grid aspect-video place-items-center bg-black">
        {isVideo ? (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <video src={st.url} autoPlay muted playsInline controls className="h-full w-full object-contain" />
        ) : (
          <img src={st.url} alt={st.name} className="h-full w-full object-contain" />
        )}
      </div>
    </div>
  );
}

/* =================================================================== */
/* Map + measurement geometry                                           */
/* =================================================================== */

const GEOM_SRC = 'analysis-geom';

function AnalysisMap({ available }: { available: Record<string, boolean> }) {
  const markersRef = useRef<maplibregl.Marker[]>([]);

  const { containerRef, mapRef } = useMapBase({
    showScale: true,
    onReady: (m) => {
      m.on('click', (e) => {
        const t = useAnalysis.getState().tool;
        if (t === 'none') return;
        useAnalysis.getState().addPoint({ lat: e.lngLat.lat, lon: e.lngLat.lng });
      });
    },
    onStyleLoaded: (m) => {
      ensureGeomLayers(m);
      syncDataLayers(m, useAnalysis.getState().layers, available);
    },
  });

  // Sketch geometry (points + line/polygon) follows the store.
  useEffect(() => {
    function sync() {
      const m = mapRef.current;
      if (!m) return;
      const { tool, points } = useAnalysis.getState();
      try {
        ensureGeomLayers(m);
        const src = m.getSource(GEOM_SRC) as maplibregl.GeoJSONSource | undefined;
        if (src) {
          const coords = points.map((p) => [p.lon, p.lat] as [number, number]);
          const features: GeoJSON.Feature[] = [];
          if (tool === 'distance' && coords.length >= 2) {
            features.push({
              type: 'Feature',
              geometry: { type: 'LineString', coordinates: coords },
              properties: {},
            });
          }
          if (tool === 'area' && coords.length >= 3) {
            features.push({
              type: 'Feature',
              geometry: { type: 'Polygon', coordinates: [[...coords, coords[0]!]] },
              properties: {},
            });
          }
          src.setData({ type: 'FeatureCollection', features });
        }
        // Numbered dots — teardown + rebuild (sketches are ≤ dozens of points).
        markersRef.current.forEach((mk) => mk.remove());
        markersRef.current = points.map((p, i) => {
          const el = document.createElement('div');
          el.style.cssText = [
            'display:grid',
            'place-items:center',
            'width:16px',
            'height:16px',
            'border-radius:50%',
            'border:1.5px solid var(--color-accent)',
            'background:rgba(14,15,17,0.85)',
            'color:var(--color-accent)',
            'font-family:var(--font-mono)',
            'font-size:8.5px',
            'font-weight:700',
            'box-sizing:border-box',
            'pointer-events:none',
          ].join(';');
          el.textContent = String(i + 1);
          return new maplibregl.Marker({ element: el, anchor: 'center' })
            .setLngLat([p.lon, p.lat])
            .addTo(m);
        });
      } catch (err) {
        console.error('[analyse] geometry sync failed', err);
      }
    }
    sync();
    const unsub = useAnalysis.subscribe(sync);
    return () => {
      unsub();
      markersRef.current.forEach((mk) => mk.remove());
      markersRef.current = [];
    };
  }, [mapRef]);

  // Data layers follow toggles.
  useEffect(() => {
    function sync() {
      const m = mapRef.current;
      if (!m || !m.isStyleLoaded()) return;
      try {
        syncDataLayers(m, useAnalysis.getState().layers, available);
      } catch (err) {
        console.error('[analyse] layer sync failed', err);
      }
    }
    sync();
    return useAnalysis.subscribe(sync);
  }, [mapRef, available]);

  const tool = useAnalysis((s) => s.tool);
  return (
    <div
      ref={containerRef}
      className={cn('h-full w-full', tool !== 'none' ? 'cursor-crosshair' : 'cursor-default')}
    />
  );
}

function ensureGeomLayers(m: maplibregl.Map) {
  if (!m.isStyleLoaded()) return;
  if (!m.getSource(GEOM_SRC)) {
    m.addSource(GEOM_SRC, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  }
  if (!m.getLayer('analysis-fill')) {
    m.addLayer({
      id: 'analysis-fill',
      type: 'fill',
      source: GEOM_SRC,
      filter: ['==', ['geometry-type'], 'Polygon'],
      paint: { 'fill-color': '#4fc8e0', 'fill-opacity': 0.12 },
    });
  }
  if (!m.getLayer('analysis-line')) {
    m.addLayer({
      id: 'analysis-line',
      type: 'line',
      source: GEOM_SRC,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#4fc8e0', 'line-width': 2, 'line-dasharray': [2, 1.5] },
    });
  }
}

/** Data layers by convention: /tiles/<stem>.pmtiles, vector source-layer =
 *  file stem. Hillshade is raster-dem. Idempotent; missing data = no-op. */
function syncDataLayers(
  m: maplibregl.Map,
  layers: AnalysisLayers,
  available: Record<string, boolean>,
) {
  // DEM hillshade
  if (layers.hillshade && available.hillshade) {
    if (!m.getSource('analysis-dem')) {
      m.addSource('analysis-dem', {
        type: 'raster-dem',
        url: `pmtiles://${window.location.origin}/tiles/dem.pmtiles`,
        tileSize: 256,
      });
    }
    if (!m.getLayer('analysis-hillshade')) {
      m.addLayer({
        id: 'analysis-hillshade',
        type: 'hillshade',
        source: 'analysis-dem',
        paint: { 'hillshade-exaggeration': 0.5 },
      });
    }
  } else if (m.getLayer('analysis-hillshade')) {
    m.removeLayer('analysis-hillshade');
  }

  const vectorSpecs = [
    { key: 'contours' as const, stem: 'contours', kind: 'line', color: '#a78bfa' },
    { key: 'water' as const, stem: 'water', kind: 'fill', color: '#38bdf8' },
    { key: 'lithology' as const, stem: 'lithology', kind: 'fill', color: '#e8a83a' },
  ];
  for (const spec of vectorSpecs) {
    const srcId = `analysis-${spec.stem}-src`;
    const layerId = `analysis-${spec.stem}`;
    if (layers[spec.key] && available[spec.key]) {
      if (!m.getSource(srcId)) {
        m.addSource(srcId, {
          type: 'vector',
          url: `pmtiles://${window.location.origin}/tiles/${spec.stem}.pmtiles`,
        });
      }
      if (!m.getLayer(layerId)) {
        m.addLayer(
          spec.kind === 'line'
            ? {
                id: layerId,
                type: 'line',
                source: srcId,
                'source-layer': spec.stem,
                paint: { 'line-color': spec.color, 'line-width': 1, 'line-opacity': 0.7 },
              }
            : {
                id: layerId,
                type: 'fill',
                source: srcId,
                'source-layer': spec.stem,
                paint: { 'fill-color': spec.color, 'fill-opacity': 0.25 },
              },
        );
      }
    } else if (m.getLayer(layerId)) {
      m.removeLayer(layerId);
    }
  }
}

/* =================================================================== */
/* Readout — bottom-centre strip with live numbers                      */
/* =================================================================== */

function MeasureReadout() {
  const tool = useAnalysis((s) => s.tool);
  const points = useAnalysis((s) => s.points);
  if (tool === 'none' || points.length === 0) return null;

  let body: React.ReactNode;
  if (tool === 'distance') {
    const total = pathLengthM(points);
    const lastLeg =
      points.length >= 2 ? distM(points[points.length - 2]!, points[points.length - 1]!) : 0;
    body = (
      <>
        <span className="text-fg-primary">{fmtDistance(total)}</span>
        <span className="text-fg-muted">total · {points.length} pts</span>
        {points.length >= 2 && <span className="text-fg-muted">last leg {fmtDistance(lastLeg)}</span>}
      </>
    );
  } else {
    const area = polygonAreaM2(points);
    const perimeter =
      points.length >= 3 ? pathLengthM([...points, points[0]!]) : pathLengthM(points);
    body =
      points.length < 3 ? (
        <span className="text-fg-muted">{3 - points.length} more corner(s) needed</span>
      ) : (
        <>
          <span className="text-fg-primary">{fmtArea(area)}</span>
          <span className="text-fg-muted">perimeter {fmtDistance(perimeter)} · {points.length} corners</span>
        </>
      );
  }

  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-8 z-10 flex justify-center px-3">
      <div className="flex items-center gap-3 rounded-sm border border-border-default bg-base/85 px-3 py-1.5 font-mono text-[10.5px] shadow-[var(--shadow-pop)] backdrop-blur-md">
        <span className="font-semibold uppercase tracking-[0.16em]" style={{ color: 'var(--color-accent)' }}>
          {tool}
        </span>
        {body}
      </div>
    </div>
  );
}

/* =================================================================== */
/* Primitives                                                           */
/* =================================================================== */

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <span
        className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em]"
        style={{ color: 'var(--color-accent)' }}
      >
        {children}
      </span>
      <span className="h-px flex-1" style={{ background: 'var(--color-accent-edge)' }} />
    </div>
  );
}

function ToolRow({
  icon,
  label,
  hint,
  active,
  disabled,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  hint: string;
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex w-full items-center gap-2 rounded border px-2 py-1.5 text-left transition-colors',
        active
          ? 'border-accent bg-accent/10'
          : 'border-border-subtle bg-surface-2/40 hover:bg-surface-2',
        disabled && 'cursor-not-allowed opacity-50 hover:bg-surface-2/40',
      )}
    >
      <span className={active ? 'text-accent' : 'text-fg-muted'}>{icon}</span>
      <span className="min-w-0 flex-1">
        <span className={cn('block text-[11.5px]', active ? 'text-accent' : 'text-fg-primary')}>
          {label}
        </span>
        <span className="block truncate text-[9.5px] text-fg-muted">{hint}</span>
      </span>
    </button>
  );
}

function Toggle({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative h-4 w-7 flex-none rounded-full border transition-colors',
        checked ? 'border-accent bg-accent/30' : 'border-border-default bg-surface-2',
        disabled && 'cursor-not-allowed opacity-40',
      )}
    >
      <span
        className={cn(
          'absolute top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full transition-[left]',
          checked ? 'left-[14px] bg-accent' : 'left-[3px] bg-fg-muted',
        )}
      />
    </button>
  );
}

const miniBtnCls =
  'flex h-6 flex-1 items-center justify-center gap-1 rounded border border-border-default bg-surface-1 font-mono text-[10px] uppercase tracking-wide text-fg-secondary hover:text-fg-primary hover:bg-surface-2';

const inputCls =
  'h-7 w-full rounded border border-border-default bg-base px-2 font-mono text-[11px] text-fg-primary placeholder:text-fg-disabled outline-none focus:border-accent';

export const analyseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/analyse',
  component: AnalyseWorkspace,
});
